import type { AssetPlacement, CreativeAction, CreativeAssignment, CreativeVersion, CreativeVersionCreateCommand, JsonObject } from "../api/types";

const MAX_PLACEMENT_VIEWPORT_SIDE = 1000;

export function placementViewport(aspectRatio: number): string {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    throw new RangeError("Placement aspect ratio must be a finite positive number.");
  }

  if (aspectRatio >= 1) {
    return `${MAX_PLACEMENT_VIEWPORT_SIDE}x${Math.max(1, Math.round(MAX_PLACEMENT_VIEWPORT_SIDE / aspectRatio))}`;
  }
  return `${Math.max(1, Math.round(MAX_PLACEMENT_VIEWPORT_SIDE * aspectRatio))}x${MAX_PLACEMENT_VIEWPORT_SIDE}`;
}

export function placementViewportList(aspectRatio: number): string[] {
  return [placementViewport(aspectRatio)];
}

export const initialDivKitDocument = (): JsonObject => ({
  card: {
    log_id: "alot_content_creative",
    states: [{
      state_id: 0,
      div: {
        type: "container",
        orientation: "vertical",
        width: { type: "match_parent" },
        height: { type: "match_parent" },
        items: [{ type: "text", text: "Новый креатив" }],
      },
    }],
  },
});

export class DivKitAdapterError extends Error {}

const ACTION_SCHEME = "alot-action://";

/** Converts legacy server-only action_id markers into native DivKit actions that Visual Editor can
 * display, change and remove through its standard Actions control. Saved source versions remain
 * immutable, so the editor always receives a defensive copy. */
export function editorDocument(document: JsonObject): JsonObject {
  const copy = structuredClone(document);
  const visit = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!value || typeof value !== "object") return;
    const object = value as Record<string, unknown>;
    const actionId = object["action_id"];
    if (typeof actionId === "string" && actionId.trim() && object["actions"] === undefined) {
      object["actions"] = [{ log_id: actionId, url: `${ACTION_SCHEME}${actionId}` }];
      delete object["action_id"];
    }
    Object.values(object).forEach(visit);
  };
  visit(copy);
  return copy;
}

export function initialEditorVersion(versions: CreativeVersion[], assignments: CreativeAssignment[]): CreativeVersion | null {
  const assignedIds = new Set(assignments.filter(assignment => assignment.weight > 0).map(assignment => assignment.creativeVersionId));
  const newest = (items: CreativeVersion[]) => items.slice().sort((left, right) => right.version - left.version)[0] ?? null;
  return newest(versions.filter(version => version.publicationState === "published" && assignedIds.has(version.id)))
    ?? newest(versions.filter(version => version.publicationState === "published"))
    ?? newest(versions);
}

/**
 * Creates an immutable version without interpreting or rewriting the document returned by the
 * official editor. DivKit compatibility belongs to the editor and platform renderer; this layer
 * only verifies the API's JSON-object contract and takes a defensive snapshot.
 */
export function buildEditorVersion(
  document: unknown,
  actions: CreativeAction[] = [],
  disclosure: JsonObject = { label: "Реклама" },
  assets: AssetPlacement[] = [],
): CreativeVersionCreateCommand {
  if (!document || typeof document !== "object" || Array.isArray(document)) {
    throw new DivKitAdapterError("DivKit JSON должен быть объектом.");
  }
  return {
    divkit: structuredClone(document) as JsonObject,
    // GET returns server-owned snapshot fields such as `partnerClick`. A saved version must be
    // adapted back to the create-command schema instead of replaying the response object: Jackson
    // rejects unknown snapshot fields before any DivKit change (for example `angle`) is persisted.
    actions: actions.filter(action => action.id.trim()).map(action => ({
      id: action.id,
      kind: action.kind,
      ...(action.url !== undefined ? { url: action.url } : {}),
      ...(action.label !== undefined ? { label: action.label } : {}),
      ...(action.signed !== undefined ? { signed: action.signed } : {}),
    })),
    disclosure: structuredClone(disclosure),
    assets: structuredClone(assets),
  };
}
